// Types
import KycQuestionnaire from '@/types/kyc-questionnaire';
import Questionnaire from '@/types/questionnaire';
import QuestionItem, { ValueMapping, OptionRangeConfig } from '@/types/question-item';
import QuestionOptionItem from '@/types/question-option-item';
import QuestionnaireSectionItem from '@/types/questionnaire-section-item';
import SavedSections from '@/types/saved-sections';

export const OTHER_QUESTION_ITEM: QuestionOptionItem = {
  _id: 'other',
  description: { key: 'other' },
  value: 1,
  hotkey: 'other',
};

export enum QUESTIONNAIRE_TYPES {
  RATIONALE = 'RATIONALE',
  RISK_PROFILE = 'RISK_PROFILE',
}

/* Gets the questionnaire that should be used by the user - prioritize enterprise questionnaires over public ones.
 * @param user Current authenticated user
 * @param questionnaires Questionnaires available for the user
 */
export const getActiveQuestionnaire = (questionnaires: Questionnaire[]) =>
  // Questionnaires a now sorted on the backend, so the enterprises ones appear first.
  questionnaires[0];

/**
 * Returns the section ID of a given question within a KYC questionnaire.
 * Used to find a question object using only the question ID.
 * @param {KycQuestionnaire} [currentQuestionnaire] - The current kyc_ questionnaire being answered.
 * @param {string} [questionId] - The id of the question whose section ID is to be found.
 * @returns the section that contains the question with the specified `questionId`.
 */
export const getSectionByQuestionId = (currentQuestionnaire?: KycQuestionnaire, questionId?: string): QuestionnaireSectionItem | undefined => {
  if (!currentQuestionnaire || !questionId) {
    return undefined;
  }
  return currentQuestionnaire.sections.find(section => section.questions.find(question => question._id === questionId));
};

/**
 * retrieves a question by its ID from a the provided questionnaire section.
 * @param {QuestionnaireSectionItem} [section] - A section within the current questionnaire
 * @param {string} [questionId] - the ID of the question to be found within a section
 * @returns the question object within the specified section
 */
export const getQuestionById = (section?: QuestionnaireSectionItem, questionId?: string): QuestionItem | undefined => {
  if (!section || !questionId) {
    return undefined;
  }

  return section.questions.find(question => question._id === questionId);
};

/**
 * Updates the `sourceQuestionsValueMapping` array of a question.
 * If the current question ID already exists in the mapping, its value is updated.
 * Otherwise, a new entry with a `question ID/question value` pair is added.
 * This mapping is used to track how source questions contribute to computing the value of a destination question in a risk questionnaire.
 * @param {QuestionItem} dependentQuestion - The destination question whose `sourceQuestionsValueMapping` will be modified.
 * @param {string} currentQuestionId - The ID of the source question contributing to the computed value of the destination question.
 * @param {number} optionValue - The source question's value, to be used in the computation.
 * @returns {Array<{ questionId: string; value: number }>} - The updated `sourceQuestionsValueMapping` array.
 */
export const updateQuestionValueMapping = (dependentQuestion: QuestionItem, currentQuestionId: string, optionValue: number) => {
  const mapping = dependentQuestion.sourceQuestionsValueMapping || [];

  const existingMapping = mapping.find(entry => entry.questionId === currentQuestionId);

  if (existingMapping) {
    existingMapping.value = optionValue;
  } else {
    mapping.push({ questionId: currentQuestionId, value: optionValue });
  }

  dependentQuestion.sourceQuestionsValueMapping = mapping;
  return mapping;
};

export const calculateQuestionTotalValue = (sourceQuestionsValueMapping: ValueMapping[]): number =>
  sourceQuestionsValueMapping.reduce((acc, questionAnswerValue) => acc + questionAnswerValue.value, 0);

/**
 * Selects the appropriate option from a question based on the sum of values provided by source questions.
 *
 * Calculates the total value from `sourceQuestionsValueMapping` and returns the first option whose defined range includes that value.
 *
 * @param {QuestionItem} question - The destination question containing a list of options, each potentially with a value range.
 * @param {ValueMapping[]} sourceQuestionsValueMapping - An array of value mappings from source questions, used to compute the total.
 * @returns {OptionRangeConfig | undefined} - The matching option whose range includes the calculated total, or `undefined` if no match is found.
 */
export const selectTargetQuestionOption = (question: QuestionItem, sourceQuestionsValueMapping: ValueMapping[]): OptionRangeConfig | undefined => {
  const totalValue = calculateQuestionTotalValue(sourceQuestionsValueMapping);

  return question.optionRangeConfig?.find(rangeConfig => rangeConfig.min <= totalValue && totalValue <= rangeConfig.max);
};

/**
 * Returns the saved section ID where a specific question is saved within the provided savedSections.
 * @param {SavedSections} savedSections - Object that contains saved sectionIds, with their
 * respective questions which have an option that has been seleted.
 * @param {string} questionId - The string that represents the unique identifier of a question.
 * @returns the section ID where the given `questionId` is saved within the `savedSections` object.
 */
export const getSavedSectionId = (savedSections: SavedSections, questionId: string) =>
  Object.keys(savedSections).find((sectionId: string) => Object.keys(savedSections[sectionId].questions).includes(questionId));
